import { useTranslation, Trans } from 'react-i18next';
import { Route, Routes, useParams, useLocation } from 'react-router-dom';
import { Head } from '~/components/shared/Head';
import Subscription from './components/Subscription';
import { MessageCounter } from '~/components/layout/Navigation.tsx/components/MessageCounter';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { motion } from 'motion/react';
import SuccessModal from './SuccessModal';
import CancelModal from './CancelModal';
import ActiveSubscription from './components/ActiveSubscription';
import { useSubscription } from '~/components/contexts/SubscriptionProvider';

const Payments = () => {
  const { t } = useTranslation('payments');
  const { agentId } = useParams();
  const location = useLocation();

  const {
    paymentsConfig,
    subscriptionData,
    activeSubscription,
    products,
    loading,
  } = useSubscription();

  // https://buy.stripe.com/test_8x200i460exjcVb7xJdwc00

  if (loading) {
    return (
      <div className="m-auto w-full max-w-[1200px] px-8 pt-12">
        <Head title={t('title')} />
        <div className="flex flex-col gap-4">
          <div className="flex flex-row items-center gap-4">
            <Skeleton width={40} height={40} circle />
            <Skeleton width={200} height={24} />
          </div>
          <div className="flex flex-row items-center gap-4">
            <Skeleton width={120} height={32} />
            <Skeleton width={300} height={20} />
          </div>
          <div className="flex flex-col gap-4">
            <Skeleton width={180} height={28} />
          </div>
        </div>
      </div>
    );
  }

  const paymentConfig = paymentsConfig;

  // Find the product associated with the active subscription (if any)
  let activeProduct = null;
  if (activeSubscription?.stripe_data?.product_id) {
    activeProduct = products?.find(
      (p: any) => p.id === activeSubscription.stripe_data.product_id,
    );
  }

  // Only show ActiveSubscription component for paid subscriptions (with stripe_data)
  const hasPaidSubscription = !!(
    activeSubscription &&
    activeSubscription.is_active &&
    activeSubscription.stripe_data?.customer_id
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="m-auto min-h-screen w-full max-w-[1200px] px-8 pt-12"
      >
        <Head title={t('title')} />
        <h1 className="mb-8 text-2xl font-bold">{t('title')}</h1>
        <div className="flex flex-col justify-center gap-4 px-4 md:flex-row md:justify-start md:gap-16">
          <div className="flex min-w-[180px] flex-col items-center gap-8">
            <MessageCounter agentId={agentId!} size="large" disabled />
          </div>
          <div className="flex-1">
            {/* DaisyUI Tabs using radio inputs */}
            <div className="tabs tabs-lift w-full">
              <label className="tab">
                <input type="radio" name="payments_tabs" defaultChecked />

                {t('subscription_options_tab', 'Subscription Options')}
              </label>
              <div className="tab-content border-base-300 bg-base-100 p-6">
                {hasPaidSubscription ? (
                  <ActiveSubscription
                    t={t}
                    agentId={agentId}
                    subscription={activeSubscription}
                    product={activeProduct}
                  />
                ) : (
                  <Subscription paymentConfig={paymentConfig} />
                )}
              </div>
              <label className="tab">
                <input type="radio" name="payments_tabs" />

                {t('info_tab', 'How it works & Info')}
              </label>
              <div className="tab-content border-base-300 bg-base-100 p-6">
                <div className="flex w-4/5 flex-col gap-4">
                  <p>{t('description')}</p>
                  <a
                    href={t('price_link')}
                    target="_blank"
                    className="link link-info"
                  >
                    {t('price_link_text')}
                  </a>
                  <h4 className="mt-4 text-lg font-bold">
                    {t('how_it_works_title')}
                  </h4>
                  <p>{t('how_it_works_description_1')}</p>
                  <h4 className="text-lg font-bold">
                    {t('what_is_a_message_title')}
                  </h4>
                  <p>
                    <Trans
                      i18nKey="what_is_a_message_description"
                      ns="payments"
                      components={{ bold: <strong /> }}
                    />
                  </p>
                  <h4 className="text-lg font-bold">
                    {t('how_to_change_plan_title')}
                  </h4>
                  <p>{t('how_to_change_plan_description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <Routes>
        <Route path="success" element={<SuccessModal />} />
        <Route path="cancel" element={<CancelModal />} />
      </Routes>
    </>
  );
};

export default Payments;
